"use client";

import { ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function EngineeredSupply() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const badgeText = "SHARJAH · AJMAN · AL AIN · GLOBAL SUPPLY · ";
  const badgeChars = badgeText.split("");
  return <section data-section-id="3139" id="engineered-supply-excellence" className="relative dark bg-slate-950 text-foreground min-h-screen w-full overflow-hidden">
      {}
      <div className="absolute inset-0 lg:hidden bg-cover bg-center opacity-30 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminium_channels_piled.webp)]" aria-hidden="true" />
      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse at center, transparent 40%, hsla(222, 47%, 7%, 0.9) 100%)"
    }} aria-hidden="true" />

      <div className="relative z-10 min-h-screen w-full flex flex-col">
        {}
        <div className="absolute top-6 right-6 md:top-10 md:right-10 z-20">
          <div className="relative w-24 h-24 md:w-32 md:h-32">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-sky-500 rounded-lg" />
            </div>
            <svg viewBox="0 0 200 200" className="w-full h-full animate-[spin_20s_linear_infinite]">
              <defs>
                <path id="circle-path" d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0" />
              </defs>
              <text className="fill-sky-500 font-mono text-[14px] tracking-[0.3em] uppercase">
                <textPath href="#circle-path" startOffset="0">
                  {badgeChars.map((char, i) => <tspan data-index={i} key={i}>
                      {char}
                    </tspan>)}
                </textPath>
              </text>
            </svg>
          </div>
        </div>

        {}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 px-6 md:px-12 lg:px-16 py-24 lg:py-20 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex items-center gap-4 mb-8 md:mb-12">
              <span className="block w-10 h-px bg-sky-500" aria-hidden="true" />
              <span className="font-mono text-xs md:text-sm uppercase tracking-[0.25em] text-sky-500">SAB PROFESSIONAL - THE PROMISE OF QUALIT & RELIABILITY</span>
            </div>

            {}
            <h1 className="font-default font-bold text-foreground leading-[0.9] mb-10 md:mb-12" style={{
            fontSize: "clamp(3.5rem, 8vw, 8rem)",
            letterSpacing: "-0.05em"
          }}>
              <span className="block">WORLD SKY</span>
              <span className="block">BUILDING MATERIALS</span>
              <span className="block text-sky-500" style={{
              textShadow: "0 0 40px hsla(199, 89%, 48%, 0.3), 0 0 80px hsla(199, 89%, 48%, 0.2)"
            }}><br /></span>
            </h1>

            {}
            <p className="font-sans text-lg md:text-xl leading-relaxed text-foreground/80 max-w-[480px] mb-10 md:mb-12">
              Premium wholesale aluminum profiles and architectural hardware. Engineered for the UAE construction landscape and global distribution.
            </p>

            {}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
              <Button asChild size="lg" className="h-14 px-8 bg-sky-500 text-white hover:bg-sky-600 font-default font-bold uppercase tracking-[0.15em] text-sm rounded-lg" style={{
              boxShadow: "0 10px 40px hsla(199, 89%, 48%, 0.2), 0 0 0 1px hsla(199, 89%, 48%, 0.1)"
            }}>
                <Link to="/ConnectWithSalesPage">
                  REQUEST QUOTE
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-8 bg-transparent border border-foreground/30 text-foreground hover:bg-foreground/10 font-default font-bold uppercase tracking-[0.15em] text-sm rounded-lg">
                <Link to="https://wa.me/971527750331" newTab>
                  <MessageCircle className="mr-2 h-4 w-4" />
                  WHATSAPP INQUIRY
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imgRef} className={`hidden lg:block lg:col-span-5 relative h-[80vh] transition-all duration-1000 ease-out delay-200 ${imgVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="absolute inset-0 -mr-16 xl:-mr-24">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/ceiling_steel_profiles.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/ceiling_steel_profiles.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/ceiling_steel_profiles.png-wvc-sizes" alt="Ceiling steel profiles" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
              filter: "grayscale(40%) contrast(1.1) brightness(0.8)"
            }} />
              {}
              <div className="absolute inset-0 pointer-events-none" style={{
              background: "radial-gradient(ellipse at center, transparent 50%, hsla(222, 47%, 7%, 0.8) 100%)"
            }} aria-hidden="true" />
            </div>
          </div>
        </div>

        {}
        <div className="absolute bottom-8 left-6 md:bottom-10 md:left-12 lg:left-16 z-20 flex flex-col items-center gap-4">
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-foreground/60 [writing-mode:vertical-rl] rotate-180">
            SCROLL
          </span>
          <div className="relative w-px h-16 md:h-20 bg-foreground/20 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-8 bg-sky-500" style={{
            animation: "scrollLine 2s ease-in-out infinite"
          }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% {
            transform: translateY(-100%);
          }
          100% {
            transform: translateY(400%);
          }
        }
      `}</style>
    </section>;
}